import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronRight, ChevronLeft, Search, Eye, History, ShieldAlert, Lock, Check, X, Minus, AlertTriangle,
  Users, HelpCircle, UserRound, Globe, UserCheck,
} from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Link, navigate } from '../router.js'
import { apiGet, adminMutate, useToast, Modal } from './primitives.js'
import { Field, TextInput } from './form.js'
import { Combobox, type ComboItem } from './pickers.js'
import { PERMISSION_SEARCH_ALIASES, normalizeSearch } from './catalog.js'
import { onUnauthenticated } from '../shell/auth-routing.js'

/**
 * PERMISSION SYSTEM V2 — UI. Ordre EXACT : USER → PERMISSIONS → FAMILLE →
 * MODULES → DÉTAILS (§29). Tuiles familles puis modules avec leur CONTRÔLE
 * PRINCIPAL en haut à droite (Hérité / Autorisé / Refusé), puis détails leaf
 * premium. Le backend décide ; ici on affiche et on propose. Impact avant
 * sauvegarde, historique, recherche, « voir comme ».
 *
 * SIRH LOT 4.7 — PÉRIMÈTRES (scopes) : pour une permission « personnes »
 * (`scopeDomain === 'PERSON'`), un sélecteur de périmètre premium sous le
 * contrôle (Lui-même / Son équipe / Tout le monde / Personnes spécifiques,
 * SELF + TEAM cumulables), l'aperçu VIVANT du périmètre (`scope-preview`),
 * l'explication « pourquoi ? » (source + périmètre + `explain` pour une
 * personne donnée), l'impact avec delta de personnes avant enregistrement,
 * l'historique avec scopes humains, et « Voir Gestion comme » enrichi des
 * données visibles. TEAM = MON PÉRIMÈTRE DE RESPONSABILITÉ, jamais
 * « mes collègues » ; il est dynamique — jamais une liste copiée.
 *
 * GARDE DE FORME : chaque réponse API est vérifiée avant usage (une erreur
 * `{ error }` ou une forme inattendue se résout en état honnête, jamais en
 * page cassée). Le core/API des périmètres est livré séparément (A1) : cette
 * UI code contre les contrats §7 ; elle n'invente jamais un domaine de
 * périmètre (`scopeDomain` vient toujours du serveur).
 *
 * SURFACES (DS §12-13, §16) : Historique et Impact sont des `Modal` (portal,
 * Échap via la pile d'overlays, focus piégé) — pas de drawer, pas d'overlay
 * maison. La Save Bar de la page est portée dans <body> : `.lc-route` porte
 * un transform, tout `position: fixed` rendu à l'intérieur est cassé.
 */

type State = 'INHERITED' | 'ALLOW' | 'DENY'
type ScopeKind = 'ALL' | 'SELF' | 'TEAM' | 'PERSON' | 'DOOR' | 'GROUP' | 'UNION'
interface Scope { type: ScopeKind; ids?: string[]; parts?: Scope[] }
type ScopeDomain = 'PERSON' | 'DOOR' | null
interface LeafView { key: string; label: string; description: string; group: string | null; risk: string | null; critical: boolean; requiresStepup: boolean; configured: State; effective: 'ALLOW' | 'DENY'; source: string; scope: Scope | null; scopeType: string | null; scopeDomain: ScopeDomain; actionBlocked: string | null }
interface ModuleView { id: string; label: string; gate: { state: State; accessible: boolean; source: string }; permissionsTotal: number; permissionsAllowed: number; leaves: LeafView[] }
interface FamilyView { id: string; label: string; gate: { state: State; accessible: boolean; source: string }; modulesAccessible: number; modulesBlocked: number; looseLeaves: LeafView[]; modules: ModuleView[] }
interface Summary { roles: { key: string; name: string }[]; directAllows: number; directDenies: number; scopes: number; lastChange: string | null }
interface SearchHit { key: string; label: string; familyId: string; familyLabel: string; moduleId: string | null; moduleLabel: string | null }

/** Un changement en attente : état posé et, pour une permission « personnes », le périmètre choisi. */
interface PendingChange { state: State; scope?: Scope | undefined }

interface ImpactLine { permissionKey: string; label: string; from: string; to: string; critical: boolean; scopeBefore?: Scope | null | undefined; scopeAfter?: Scope | null | undefined; deltaPeople?: number | null | undefined }
interface Impact { lines: ImpactLine[]; affectedUsers: number | null; affectedUserScopes: { id: string; name: string; count: number | null }[]; criticalCount: number; reasonRequired: boolean; stepUpRequired: boolean; blocked: string | null }

interface ScopePart { kind: 'ALL' | 'SELF' | 'TEAM' | 'PERSON'; count: number | null; teams?: { team_id: string; name: string; count: number }[] | undefined; direct?: number | undefined; ids?: string[] | undefined }
interface ScopePerson { person_id: string; display_name: string; via: string[] }
interface ScopePreview { count: number | null; parts: ScopePart[]; people: ScopePerson[]; affectedUsers: { id: string; name: string; count: number | null }[] }

const RISK_LABEL: Record<string, string> = { LOW: 'Faible', MEDIUM: 'Moyen', HIGH: 'Élevé', CRITICAL: 'Critique' }

/** Doctrine TEAM (gelée, Lot 4.7 §1) — libellé produit affiché AU MOT PRÈS sous le sélecteur… */
const TEAM_DOCTRINE = 'Personnes actuellement rattachées aux équipes et responsabilités dont cet utilisateur est responsable.'
/** …suivi d'une phrase explicite (hint 13 px) : ce que TEAM contient, et ce qu'il ne contient jamais. */
const TEAM_DOCTRINE_HINT = 'Membres actifs des équipes qu’il dirige et personnes qui lui sont directement rattachées, évalués aujourd’hui — jamais ses collègues, jamais lui-même.'
/** En mode RÔLE, « cet utilisateur » / « lui-même » deviennent « le porteur du rôle » / « le porteur ». */
function teamDoctrine(roleMode: boolean): string { return roleMode ? TEAM_DOCTRINE.replace('cet utilisateur', 'le porteur du rôle') : TEAM_DOCTRINE }
function teamDoctrineHint(roleMode: boolean): string { return roleMode ? TEAM_DOCTRINE_HINT.replace('lui-même', 'le porteur') : TEAM_DOCTRINE_HINT }

/* ─────────── garde de forme (réponses API) ─────────── */
function isRecord(v: unknown): v is Record<string, unknown> { return typeof v === 'object' && v !== null }
function hasError(v: unknown): v is { error: string; message?: string } { return isRecord(v) && typeof v['error'] === 'string' }
function isScope(v: unknown): v is Scope { return isRecord(v) && typeof v['type'] === 'string' }
function asScope(v: unknown): Scope | null { return isScope(v) ? v : null }

type Shaped<T> = { ok: true; data: T } | { ok: false; error: string; message?: string | undefined }
/** GET avec garde de forme : `{ error }` et forme inattendue deviennent un résultat honnête. */
async function getShaped<T>(path: string, shape: (v: unknown) => T | null): Promise<Shaped<T>> {
  try {
    const r = await apiGet<unknown>(path)
    if (hasError(r)) return { ok: false, error: r.error, message: r.message }
    const data = shape(r)
    return data ? { ok: true, data } : { ok: false, error: 'BAD_SHAPE', message: 'Réponse inattendue du serveur.' }
  } catch { return { ok: false, error: 'NETWORK', message: 'Le serveur n’a pas répondu.' } }
}

/** Le serveur peut renvoyer la forme USER (configured/effective) ou la forme RÔLE (state) — on normalise. */
function shapeLeaf(v: unknown): LeafView | null {
  if (!isRecord(v) || typeof v['key'] !== 'string') return null
  const stateRaw = (v['configured'] ?? v['state']) as unknown
  const configured: State = stateRaw === 'ALLOW' || stateRaw === 'DENY' ? stateRaw : 'INHERITED'
  const effectiveRaw = v['effective']
  const effective: 'ALLOW' | 'DENY' = effectiveRaw === 'ALLOW' || effectiveRaw === 'DENY' ? effectiveRaw : configured === 'ALLOW' ? 'ALLOW' : 'DENY'
  const scopeType = typeof v['scopeType'] === 'string' ? v['scopeType'] : null
  const domainRaw = v['scopeDomain']
  // Le domaine vient TOUJOURS du serveur (`permission.scope_domain`, migration Lot 4.7) : aucun repli
  // déduit de `scopeType` — le web n'invente jamais un domaine.
  const scopeDomain: ScopeDomain = domainRaw === 'PERSON' || domainRaw === 'DOOR' ? domainRaw : null
  return {
    key: v['key'], label: typeof v['label'] === 'string' ? v['label'] : v['key'], description: typeof v['description'] === 'string' ? v['description'] : '',
    group: typeof v['group'] === 'string' ? v['group'] : null, risk: typeof v['risk'] === 'string' ? v['risk'] : null,
    critical: v['critical'] === true, requiresStepup: v['requiresStepup'] === true, configured, effective,
    source: typeof v['source'] === 'string' ? v['source'] : '', scope: asScope(v['scope']), scopeType, scopeDomain,
    actionBlocked: typeof v['actionBlocked'] === 'string' ? v['actionBlocked'] : null,
  }
}
function shapeGate(v: unknown): { state: State; accessible: boolean; source: string } {
  if (typeof v === 'string') return { state: v === 'ALLOW' || v === 'DENY' ? v : 'INHERITED', accessible: v !== 'DENY', source: '' }
  if (isRecord(v)) { const s = v['state']; return { state: s === 'ALLOW' || s === 'DENY' ? s : 'INHERITED', accessible: v['accessible'] !== false, source: typeof v['source'] === 'string' ? v['source'] : '' } }
  return { state: 'INHERITED', accessible: true, source: '' }
}
function shapeLeaves(v: unknown): LeafView[] { return Array.isArray(v) ? v.map(shapeLeaf).filter((l): l is LeafView => l !== null) : [] }
function shapeTree(v: unknown): FamilyView[] | null {
  if (!Array.isArray(v)) return null
  const out: FamilyView[] = []
  for (const f of v) {
    if (!isRecord(f) || typeof f['id'] !== 'string') continue
    const modules: ModuleView[] = (Array.isArray(f['modules']) ? f['modules'] : []).flatMap((m: unknown) => {
      if (!isRecord(m) || typeof m['id'] !== 'string') return []
      const leaves = shapeLeaves(m['leaves'])
      return [{ id: m['id'], label: typeof m['label'] === 'string' ? m['label'] : m['id'], gate: shapeGate(m['gate']),
        permissionsTotal: typeof m['permissionsTotal'] === 'number' ? m['permissionsTotal'] : leaves.length,
        permissionsAllowed: typeof m['permissionsAllowed'] === 'number' ? m['permissionsAllowed'] : leaves.filter((l) => l.effective === 'ALLOW').length, leaves }]
    })
    out.push({ id: f['id'], label: typeof f['label'] === 'string' ? f['label'] : f['id'], gate: shapeGate(f['gate']),
      modulesAccessible: typeof f['modulesAccessible'] === 'number' ? f['modulesAccessible'] : modules.filter((m) => m.gate.accessible).length,
      modulesBlocked: typeof f['modulesBlocked'] === 'number' ? f['modulesBlocked'] : modules.filter((m) => !m.gate.accessible).length,
      looseLeaves: shapeLeaves(f['looseLeaves']), modules })
  }
  return out
}
function shapeSummary(v: unknown): Summary | null {
  if (!isRecord(v) || !Array.isArray(v['roles'])) return null
  return {
    roles: (v['roles'] as unknown[]).flatMap((r) => isRecord(r) && typeof r['name'] === 'string' ? [{ key: String(r['key'] ?? r['name']), name: r['name'] }] : []),
    directAllows: typeof v['directAllows'] === 'number' ? v['directAllows'] : 0, directDenies: typeof v['directDenies'] === 'number' ? v['directDenies'] : 0,
    scopes: typeof v['scopes'] === 'number' ? v['scopes'] : 0, lastChange: typeof v['lastChange'] === 'string' ? v['lastChange'] : null,
  }
}
function shapeScopePreview(v: unknown): ScopePreview | null {
  if (!isRecord(v) || !Array.isArray(v['parts'])) return null
  const parts = (v['parts'] as unknown[]).flatMap((p): ScopePart[] => {
    if (!isRecord(p) || typeof p['kind'] !== 'string') return []
    const kind = p['kind']; if (kind !== 'ALL' && kind !== 'SELF' && kind !== 'TEAM' && kind !== 'PERSON') return []
    return [{ kind, count: typeof p['count'] === 'number' ? p['count'] : null,
      teams: Array.isArray(p['teams']) ? (p['teams'] as unknown[]).flatMap((t) => isRecord(t) && typeof t['name'] === 'string' ? [{ team_id: String(t['team_id'] ?? t['name']), name: t['name'], count: typeof t['count'] === 'number' ? t['count'] : 0 }] : []) : undefined,
      direct: typeof p['direct'] === 'number' ? p['direct'] : undefined, ids: Array.isArray(p['ids']) ? (p['ids'] as unknown[]).map(String) : undefined }]
  })
  const people = Array.isArray(v['people']) ? (v['people'] as unknown[]).flatMap((x): ScopePerson[] => isRecord(x) && typeof x['display_name'] === 'string' ? [{ person_id: String(x['person_id'] ?? x['display_name']), display_name: x['display_name'], via: Array.isArray(x['via']) ? (x['via'] as unknown[]).map(String) : [] }] : []) : []
  return { count: typeof v['count'] === 'number' ? v['count'] : null, parts, people, affectedUsers: shapeAffected(v['affectedUsers']) }
}
/** Porteurs d'un rôle avec leur compte individuel (`userId` côté API ; `id` toléré). */
function shapeAffected(v: unknown): { id: string; name: string; count: number | null }[] {
  return Array.isArray(v) ? (v as unknown[]).flatMap((x) => isRecord(x) && typeof x['name'] === 'string' ? [{ id: String(x['userId'] ?? x['id'] ?? x['name']), name: x['name'], count: typeof x['count'] === 'number' ? x['count'] : null }] : []) : []
}

/* ─────────── périmètres : libellés humains ─────────── */
function plural(n: number, one: string, many: string): string { return `${n} ${n > 1 ? many : one}` }
/** Libellé humain d'un périmètre. `null` = aucune restriction (ALL). */
function scopeLabel(scope: Scope | null | undefined): string {
  if (!scope) return 'Tout le monde'
  switch (scope.type) {
    case 'ALL': return 'Tout le monde'
    case 'SELF': return 'Lui-même'
    case 'TEAM': return 'Son équipe'
    case 'PERSON': return plural(scope.ids?.length ?? 0, 'personne', 'personnes')
    case 'UNION': return (scope.parts ?? []).map(scopeLabel).join(' + ') || 'Tout le monde'
    case 'DOOR': return plural(scope.ids?.length ?? 0, 'porte', 'portes')
    case 'GROUP': return plural(scope.ids?.length ?? 0, 'groupe', 'groupes')
    default: return String(scope.type)
  }
}
function scopeIsAll(scope: Scope | null | undefined): boolean { return !scope || scope.type === 'ALL' }
function scopeEq(a: Scope | null | undefined, b: Scope | null | undefined): boolean { return JSON.stringify(a ?? { type: 'ALL' }) === JSON.stringify(b ?? { type: 'ALL' }) }

type PersonKind = 'SELF' | 'TEAM' | 'ALL' | 'PERSON'
/** Décompose un périmètre « personnes » en composantes sélectionnées. */
function kindsOf(scope: Scope | null | undefined): { kinds: Set<PersonKind>; ids: string[] } {
  if (!scope || scope.type === 'ALL') return { kinds: new Set(['ALL']), ids: [] }
  if (scope.type === 'UNION') { const k = new Set<PersonKind>(); let ids: string[] = []; for (const p of scope.parts ?? []) { const s = kindsOf(p); s.kinds.forEach((x) => k.add(x)); ids = [...ids, ...s.ids] }; if (k.size === 0) k.add('ALL'); return { kinds: k, ids } }
  if (scope.type === 'PERSON') return { kinds: new Set(['PERSON']), ids: scope.ids ?? [] }
  if (scope.type === 'SELF' || scope.type === 'TEAM') return { kinds: new Set([scope.type]), ids: [] }
  return { kinds: new Set(['ALL']), ids: [] }
}
/** Recompose un périmètre : SELF + TEAM → UNION ; PERSON avec ids ; ALL. */
function scopeOf(kinds: Set<PersonKind>, ids: string[]): Scope {
  if (kinds.has('ALL')) return { type: 'ALL' }
  if (kinds.has('PERSON')) return { type: 'PERSON', ids }
  const parts: Scope[] = []
  if (kinds.has('SELF')) parts.push({ type: 'SELF' })
  if (kinds.has('TEAM')) parts.push({ type: 'TEAM' })
  if (parts.length === 1) return parts[0]!
  if (parts.length === 0) return { type: 'ALL' }
  return { type: 'UNION', parts }
}
/** Le périmètre mérite un aperçu vivant : il dépend des équipes ou d'une liste. */
function scopeNeedsPreview(scope: Scope | null | undefined): boolean {
  const { kinds, ids } = kindsOf(scope)
  return kinds.has('TEAM') || (kinds.has('PERSON') && ids.length > 0)
}

/* ─────────── salariés (Combobox « Personnes spécifiques », « Pourquoi cette personne ? ») ─────────── */
/** États EXPLICITES : un échec (403 sans « Consulter les salariés », 500, réseau) n'est JAMAIS confondu
 *  avec une liste vide — plus de « Chargement des salariés… » à vie. */
interface PeopleState { status: 'loading' | 'ok' | 'empty' | 'error'; items: ComboItem[]; error?: string | undefined }
let peopleCache: PeopleState | null = null // seuls `ok` / `empty` sont mémorisés : une erreur se retente
let peopleInFlight: Promise<PeopleState> | null = null
function loadPeople(): Promise<PeopleState> {
  if (peopleCache) return Promise.resolve(peopleCache)
  if (peopleInFlight) return peopleInFlight
  peopleInFlight = getShaped('/api/v1/rh/people', (v) => isRecord(v) && Array.isArray(v['people']) ? (v['people'] as unknown[]).flatMap((p): ComboItem[] => isRecord(p) && typeof p['id'] === 'string' && typeof p['display_name'] === 'string' ? [{ value: p['id'], label: p['display_name'], ...(typeof p['position'] === 'string' ? { hint: p['position'] } : {}) }] : []) : null)
    .then((r): PeopleState => {
      if (!r.ok) return { status: 'error', items: [], error: r.error }
      const s: PeopleState = r.data.length ? { status: 'ok', items: r.data } : { status: 'empty', items: [] }
      peopleCache = s
      return s
    })
    .finally(() => { peopleInFlight = null })
  return peopleInFlight
}
function usePeople(enabled: boolean): PeopleState {
  const [state, setState] = useState<PeopleState>(peopleCache ?? { status: 'loading', items: [] })
  useEffect(() => { if (!enabled) return; let alive = true; void loadPeople().then((s) => { if (alive) setState(s) }); return () => { alive = false } }, [enabled])
  return state
}
/** Texte honnête quand la liste n'est pas exploitable : indisponible (et pourquoi) ou vide. */
function peopleUnavailableLabel(s: PeopleState): string {
  if (s.status === 'error') return s.error === 'FORBIDDEN' ? 'Salariés indisponibles — droit « Consulter les salariés » requis' : 'Salariés indisponibles'
  return 'Aucun salarié'
}
/** Combobox salariés OU son remplaçant textuel selon l'état (DS §8 : un contrôle inutilisable est absent, pas grisé). */
function PeoplePicker({ people, value, items, onChange, placeholder, ariaLabel, clearable, leading }: { people: PeopleState; value: string | null; items: ComboItem[]; onChange: (id: string | null) => void; placeholder: string; ariaLabel: string; clearable?: boolean | undefined; leading: ReactNode }) {
  if (people.status === 'error') return <span className="pm-scope__unavailable" role="status"><AlertTriangle size={13} aria-hidden="true" /> {peopleUnavailableLabel(people)}</span>
  if (people.status === 'empty') return <span className="lc-hint" role="status">{peopleUnavailableLabel(people)}</span>
  return <Combobox value={value} items={items} onChange={onChange} {...(clearable ? { clearable: true } : {})} placeholder={people.status === 'loading' ? 'Chargement des salariés…' : placeholder} ariaLabel={ariaLabel} leading={leading} />
}

/* ─────────── aperçu vivant d'un périmètre (scope-preview) ─────────── */
interface PreviewState { status: 'idle' | 'loading' | 'ok' | 'error'; data?: ScopePreview | undefined; message?: string | undefined }
function useScopePreview(targetType: 'USER' | 'ROLE', targetId: string, permission: string, scope: Scope | null, enabled: boolean): PreviewState {
  const [state, setState] = useState<PreviewState>({ status: 'idle' })
  const scopeKey = JSON.stringify(scope)
  useEffect(() => {
    if (!enabled || !scope) { setState({ status: 'idle' }); return }
    let alive = true
    setState({ status: 'loading' })
    const t = setTimeout(() => {
      void getShaped(`/api/v1/admin/permissions/scope-preview?targetType=${targetType}&targetId=${encodeURIComponent(targetId)}&permission=${encodeURIComponent(permission)}&scope=${encodeURIComponent(scopeKey)}`, shapeScopePreview)
        .then((r) => { if (!alive) return; setState(r.ok ? { status: 'ok', data: r.data } : { status: 'error', message: r.message ?? r.error }) })
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [targetType, targetId, permission, scopeKey, enabled]) // eslint-disable-line react-hooks/exhaustive-deps
  return state
}

/* ─────────── contrôle de gate (Hérité / Autorisé / Refusé) ─────────── */
function GateControl({ value, onChange, roleMode }: { value: State; onChange: (s: State) => void; roleMode?: boolean }) {
  const opts: { s: State; label: string; icon: ReactNode }[] = roleMode
    ? [{ s: 'INHERITED', label: 'Neutre', icon: <Minus size={13} /> }, { s: 'ALLOW', label: 'Autorisé', icon: <Check size={13} /> }, { s: 'DENY', label: 'Refusé', icon: <X size={13} /> }]
    : [{ s: 'INHERITED', label: 'Hérité', icon: <Minus size={13} /> }, { s: 'ALLOW', label: 'Autorisé', icon: <Check size={13} /> }, { s: 'DENY', label: 'Refusé', icon: <X size={13} /> }]
  return (
    <div className="pm-gate" role="group" aria-label="Contrôle d’accès" onClick={(e) => e.stopPropagation()}>
      {opts.map((o) => (
        <button key={o.s} type="button" className={`pm-gate__opt pm-gate__opt--${o.s.toLowerCase()} ${value === o.s ? 'is-on' : ''}`} aria-pressed={value === o.s} onClick={() => onChange(o.s)}>{o.icon}<span>{o.label}</span></button>
      ))}
    </div>
  )
}

/* ═══════════════ page utilisateur ═══════════════ */
export function UserPermissionsPage({ id }: { id: string }) {
  return <PermissionsWorkbench targetType="USER" targetId={id} backHref={`/settings/users/${id}`} />
}
export function RolePermissionsPage({ roleKey }: { roleKey: string }) {
  return <PermissionsWorkbench targetType="ROLE" targetId={roleKey} backHref="/settings/roles" />
}

/* ─────────── recherche locale (fallback quand le serveur ne renvoie rien) ─────────── */
function localSearch(q: string, families: FamilyView[] | null): SearchHit[] {
  const nq = normalizeSearch(q)
  if (nq.length < 2) return []
  const hits: SearchHit[] = []
  const seen = new Set<string>()
  const push = (h: SearchHit) => { if (!seen.has(h.key)) { seen.add(h.key); hits.push(h) } }
  const fams = families ?? []
  const famById = (id: string) => fams.find((f) => f.id === id) ?? null
  const matches = (term: string) => term.startsWith(nq) || nq.includes(term)
  for (const a of PERMISSION_SEARCH_ALIASES) {
    if (!a.terms.some(matches)) continue
    const fam = famById(a.familyId)
    const mod = a.moduleId ? fam?.modules.find((m) => m.id === a.moduleId) ?? null : null
    const familyLabel = fam?.label ?? a.familyId
    if (a.moduleId) push({ key: `gate.module.${a.familyId}.${a.moduleId.replace(/-/g, '_')}`, label: mod?.label ?? a.moduleId, familyId: a.familyId, familyLabel, moduleId: a.moduleId, moduleLabel: mod?.label ?? a.moduleId })
    else push({ key: `gate.family.${a.familyId}`, label: familyLabel, familyId: a.familyId, familyLabel, moduleId: null, moduleLabel: null })
    for (const l of mod?.leaves ?? []) if ((a.keyPrefixes ?? []).some((p) => l.key.startsWith(p))) push({ key: l.key, label: l.label, familyId: a.familyId, familyLabel, moduleId: a.moduleId, moduleLabel: mod?.label ?? null })
  }
  for (const f of fams) {
    if (normalizeSearch(f.label).includes(nq)) push({ key: `gate.family.${f.id}`, label: f.label, familyId: f.id, familyLabel: f.label, moduleId: null, moduleLabel: null })
    for (const l of f.looseLeaves) if (normalizeSearch(`${l.label} ${l.description}`).includes(nq)) push({ key: l.key, label: l.label, familyId: f.id, familyLabel: f.label, moduleId: null, moduleLabel: null })
    for (const m of f.modules) {
      if (normalizeSearch(m.label).includes(nq)) push({ key: `gate.module.${f.id}.${m.id.replace(/-/g, '_')}`, label: m.label, familyId: f.id, familyLabel: f.label, moduleId: m.id, moduleLabel: m.label })
      for (const l of m.leaves) if (normalizeSearch(`${l.label} ${l.description}`).includes(nq)) push({ key: l.key, label: l.label, familyId: f.id, familyLabel: f.label, moduleId: m.id, moduleLabel: m.label })
    }
  }
  return hits.slice(0, 40)
}
function shapeHits(v: unknown): SearchHit[] | null {
  if (!isRecord(v) || !Array.isArray(v['hits'])) return null
  return (v['hits'] as unknown[]).flatMap((h): SearchHit[] => isRecord(h) && typeof h['key'] === 'string' && typeof h['familyId'] === 'string'
    ? [{ key: h['key'], label: typeof h['label'] === 'string' ? h['label'] : h['key'], familyId: h['familyId'], familyLabel: typeof h['familyLabel'] === 'string' ? h['familyLabel'] : h['familyId'], moduleId: typeof h['moduleId'] === 'string' ? h['moduleId'] : null, moduleLabel: typeof h['moduleLabel'] === 'string' ? h['moduleLabel'] : null }] : [])
}

function PermissionsWorkbench({ targetType, targetId, backHref }: { targetType: 'USER' | 'ROLE'; targetId: string; backHref: string }) {
  const meta = useShellMeta()
  const toast = useToast()
  const [families, setFamilies] = useState<FamilyView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [openFam, setOpenFam] = useState<string | null>(null)
  const [openMod, setOpenMod] = useState<string | null>(null)
  const [pending, setPending] = useState<Map<string, PendingChange>>(new Map())
  const [impact, setImpact] = useState<Impact | null>(null)
  // `impactOpen` pilote la Modal ; `impact` survit à la fermeture le temps de la sortie animée (onExited le vide).
  const [impactOpen, setImpactOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState<string | null>(null)
  const [searchQ, setSearchQ] = useState('')
  const [searchHits, setSearchHits] = useState<SearchHit[]>([])
  const [name, setName] = useState<string>('')
  const [roleProtected, setRoleProtected] = useState(false)
  const familiesRef = useRef<FamilyView[] | null>(null)
  familiesRef.current = families

  const load = () => {
    setLoadError(null)
    if (targetType === 'USER') {
      void getShaped(`/api/v1/admin/users/${targetId}/permissions`, (v) => { const tree = isRecord(v) ? shapeTree(v['tree']) : null; return tree ? { tree, summary: isRecord(v) ? shapeSummary(v['summary']) : null } : null })
        .then((r) => { if (r.ok) { setFamilies(r.data.tree); setSummary(r.data.summary) } else { setFamilies([]); setLoadError(r.message ?? r.error) } })
      void getShaped(`/api/v1/admin/users/${targetId}`, (v) => isRecord(v) && isRecord(v['user']) && typeof v['user']['display_name'] === 'string' ? v['user']['display_name'] : null).then((r) => setName(r.ok ? r.data : ''))
    } else {
      void getShaped(`/api/v1/admin/roles/${targetId}/permissions`, (v) => isRecord(v) ? shapeTree(v['tree']) : null)
        .then((r) => { if (r.ok) setFamilies(r.data); else { setFamilies([]); setLoadError(r.message ?? r.error) } })
      // Le NOM du modèle (pas sa clé technique) et sa protection : le Super
      // Admin se lit ici mais ne s'écrit pas (409 ROLE_PROTECTED côté serveur).
      void getShaped(`/api/v1/admin/roles/${targetId}`, (v) => isRecord(v) && typeof v['name'] === 'string' ? { name: v['name'], protected: v['protected'] === true } : null)
        .then((r) => { setName(r.ok ? r.data.name : targetId); setRoleProtected(r.ok ? r.data.protected : targetId === 'super_admin') })
    }
  }
  useEffect(load, [targetType, targetId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchQ.trim().length < 2) { setSearchHits([]); return }
    const t = setTimeout(() => void getShaped(`/api/v1/admin/permissions/search?q=${encodeURIComponent(searchQ)}`, shapeHits)
      .then((r) => { const hits = r.ok ? r.data : []; setSearchHits(hits.length ? hits : localSearch(searchQ, familiesRef.current)) }), 200)
    return () => clearTimeout(t)
  }, [searchQ])

  /* Changement d'ÉTAT : revenir à l'état posé = plus rien en attente (un périmètre choisi
     survit seulement si l'état reste Autorisé ; « Hérité » abandonne l'override, scope compris). */
  const setChange = (key: string, current: State, next: State) => {
    // Modèle protégé : rien ne se met en attente (le serveur refuserait).
    if (roleProtected) return
    setPending((prev) => {
      const m = new Map(prev)
      const before = prev.get(key)
      if (next === current) {
        if (next === 'ALLOW' && before?.scope !== undefined) return prev
        m.delete(key); return m
      }
      if (next !== 'ALLOW') m.set(key, { state: next })
      else m.set(key, { state: next, ...(before?.scope !== undefined ? { scope: before.scope } : {}) })
      return m
    })
  }
  /* Changement de PÉRIMÈTRE : l'état devient ALLOW (override de scope sur un droit hérité, §84) ;
     retomber sur le périmètre courant avec l'état d'origine = plus rien en attente. */
  const setScope = (leaf: LeafView, scope: Scope) => {
    if (roleProtected) return
    setPending((prev) => {
      const m = new Map(prev)
      const before = prev.get(leaf.key)
      const stateNow = before?.state ?? leaf.configured
      if (scopeEq(scope, leaf.scope) && stateNow === leaf.configured) m.delete(leaf.key)
      else m.set(leaf.key, { state: 'ALLOW', scope })
      return m
    })
  }

  const effectiveState = (key: string, current: State): State => pending.get(key)?.state ?? current
  const pendingScope = (key: string): Scope | undefined => pending.get(key)?.scope

  const changesBody = () => [...pending.entries()].map(([permissionKey, c]) => ({ permissionKey, state: c.state, ...(c.scope !== undefined ? { scope: c.scope } : {}) }))
  const doPreview = async () => {
    const changes = changesBody()
    if (changes.length === 0) return
    const r = await adminMutate<{ error?: string; message?: string; lines?: unknown; affectedUsers?: unknown; affectedUserScopes?: unknown; criticalCount?: unknown; reasonRequired?: unknown; stepUpRequired?: unknown; blocked?: unknown }>('POST', '/api/v1/admin/permissions/preview', { targetType, targetId, changes })
    if (r.status >= 400 || !Array.isArray(r.data.lines)) { toast('error', r.data.message ?? r.data.error ?? 'Aperçu d’impact indisponible.'); return }
    const lines = (r.data.lines as unknown[]).flatMap((l): ImpactLine[] => isRecord(l) && typeof l['permissionKey'] === 'string'
      ? [{ permissionKey: l['permissionKey'], label: typeof l['label'] === 'string' ? l['label'] : l['permissionKey'], from: String(l['from'] ?? 'INHERITED'), to: String(l['to'] ?? 'INHERITED'), critical: l['critical'] === true,
        scopeBefore: 'scopeBefore' in l ? asScope(l['scopeBefore']) : undefined, scopeAfter: 'scopeAfter' in l ? asScope(l['scopeAfter']) : undefined,
        deltaPeople: typeof l['deltaPeople'] === 'number' ? l['deltaPeople'] : l['deltaPeople'] === null ? null : undefined }] : [])
    const blocked = isRecord(r.data.blocked) && typeof r.data.blocked['message'] === 'string' ? r.data.blocked['message'] : null
    setImpact({ lines, affectedUsers: typeof r.data.affectedUsers === 'number' ? r.data.affectedUsers : null, affectedUserScopes: shapeAffected(r.data.affectedUserScopes), criticalCount: typeof r.data.criticalCount === 'number' ? r.data.criticalCount : 0, reasonRequired: r.data.reasonRequired === true, stepUpRequired: r.data.stepUpRequired === true, blocked })
    setImpactOpen(true)
  }
  const doApply = async () => {
    const changes = changesBody()
    setSaving(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/admin/permissions/apply', { targetType, targetId, changes, reason: reason || null })
    setSaving(false)
    if (r.status >= 400) { toast('error', r.data.message ?? r.data.error ?? 'Échec.'); return }
    toast('ok', 'Permissions enregistrées.')
    setPending(new Map()); setImpactOpen(false); setReason(''); load()
  }
  const cancelAll = () => { setPending(new Map()); setImpactOpen(false) }

  const roleMode = targetType === 'ROLE'
  const currentFam = families?.find((f) => f.id === openFam) ?? null
  const currentMod = currentFam?.modules.find((m) => m.id === openMod) ?? null
  const dirty = pending.size > 0
  const leafProps = { roleMode, targetType, targetId, effectiveState, pendingScope, onChange: setChange, onScope: setScope, onHistory: setHistoryKey }
  const historyLabel = historyKey ? leafLabelOf(families, historyKey) : null

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger pm-page">
        <Link className="lc-crumb" href={backHref}><ChevronLeft size={15} aria-hidden="true" /> {roleMode ? 'Modèles de droits' : 'Utilisateur'}</Link>
        <header className="pm-head">
          <div>
            <h1 className="lc-title">{roleMode ? 'Modèle' : 'Permissions'}{name ? ` — ${name}` : ''}</h1>
            <p className="lc-subtitle">{roleMode ? 'Les droits de ce modèle, famille par famille. Les comptes qui l’appliquent en héritent immédiatement.' : 'Les droits effectifs de cet utilisateur, famille par famille. Une case posée ici est une exception : elle l’emporte sur ses modèles.'}</p>
          </div>
          {targetType === 'USER' ? <button type="button" className="pm-viewas" onClick={() => navigate(`/settings/users/${targetId}/preview`)}><Eye size={15} aria-hidden="true" /> Voir Gestion comme cet utilisateur</button> : null}
        </header>

        {summary ? (
          <div className="pm-summary">
            <span><strong>{summary.roles.length}</strong> rôle(s){summary.roles.length ? ` : ${summary.roles.map((r) => r.name).join(', ')}` : ''}</span>
            <span><strong>{summary.directAllows}</strong> droit(s) direct(s)</span>
            <span><strong>{summary.directDenies}</strong> refus direct(s)</span>
            <span><strong>{summary.scopes}</strong> périmètre(s)</span>
            {summary.lastChange ? <span>Dernière modification : {new Date(summary.lastChange).toLocaleString('fr-FR')}</span> : null}
          </div>
        ) : null}

        <div className="pm-searchbar">
          <Search size={16} aria-hidden="true" />
          <input placeholder="Rechercher une permission (ex. Équipe, Planning, Absences, Validation, Compteurs)…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
          {searchHits.length > 0 ? (
            <div className="pm-search-results">
              {searchHits.map((h) => (
                <button key={h.key} type="button" className="pm-search-hit" onClick={() => { setOpenFam(h.familyId); setOpenMod(h.moduleId); setSearchQ(''); setSearchHits([]); setTimeout(() => document.getElementById(`leaf-${h.key}`)?.scrollIntoView({ block: 'center' }), 60); setHistoryKey(null) }}>
                  <strong>{h.label}</strong><span>{h.familyLabel}{h.moduleLabel ? ` › ${h.moduleLabel}` : ''}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {loadError ? <p className="pm-error" role="alert"><AlertTriangle size={14} aria-hidden="true" /> Permissions indisponibles : {loadError}</p> : null}
        {roleMode && roleProtected ? <p className="pm-error" role="note"><Lock size={14} aria-hidden="true" /> Modèle protégé : le Super Admin possède tout le catalogue et ne se modifie pas depuis l’interface.</p> : null}

        {/* Niveau 1 : familles */}
        {!currentFam ? (
          <section className="pm-tiles lc-stagger">
            {(families ?? []).map((f) => {
              const st = effectiveState(`gate.family.${f.id}`, f.gate.state)
              return (
                <button key={f.id} type="button" className={`pm-tile ${st === 'DENY' ? 'pm-tile--denied' : ''}`} onClick={() => { setOpenFam(f.id); setOpenMod(null) }}>
                  <div className="pm-tile__top"><strong>{f.label}</strong><GateControl value={st} roleMode={roleMode} onChange={(s) => setChange(`gate.family.${f.id}`, f.gate.state, s)} /></div>
                  <span className="pm-tile__sub">{f.modulesAccessible} module(s) accessible(s){f.modulesBlocked ? ` · ${f.modulesBlocked} bloqué(s)` : ''}</span>
                  <span className="pm-tile__source">{f.gate.source}</span>
                  <ChevronRight size={16} className="pm-tile__chev" aria-hidden="true" />
                </button>
              )
            })}
            {!families ? <p className="lc-hint">Chargement…</p> : null}
          </section>
        ) : !currentMod ? (
          /* Niveau 2 : modules d'une famille */
          <>
            <button type="button" className="pm-back" onClick={() => setOpenFam(null)}><ChevronLeft size={14} aria-hidden="true" /> Familles</button>
            <h2 className="pm-level-title">{currentFam.label}</h2>
            {currentFam.looseLeaves.length ? <LeafSection title="Général" leaves={currentFam.looseLeaves} {...leafProps} /> : null}
            <section className="pm-tiles lc-stagger">
              {currentFam.modules.map((m) => {
                const st = effectiveState(`gate.module.${currentFam.id}.${m.id.replace(/-/g, '_')}`, m.gate.state)
                return (
                  <button key={m.id} type="button" className={`pm-tile ${st === 'DENY' ? 'pm-tile--denied' : ''}`} onClick={() => setOpenMod(m.id)}>
                    <div className="pm-tile__top"><strong>{m.label}</strong><GateControl value={st} roleMode={roleMode} onChange={(s) => setChange(`gate.module.${currentFam.id}.${m.id.replace(/-/g, '_')}`, m.gate.state, s)} /></div>
                    <span className="pm-tile__sub">{m.permissionsAllowed}/{m.permissionsTotal} permission(s)</span>
                    <span className="pm-tile__source">{m.gate.source}</span>
                    <ChevronRight size={16} className="pm-tile__chev" aria-hidden="true" />
                  </button>
                )
              })}
            </section>
          </>
        ) : (
          /* Niveau 3 : détails du module */
          <>
            <button type="button" className="pm-back" onClick={() => setOpenMod(null)}><ChevronLeft size={14} aria-hidden="true" /> {currentFam.label}</button>
            <h2 className="pm-level-title">{currentFam.label} › {currentMod.label}</h2>
            <GroupedLeaves leaves={currentMod.leaves} {...leafProps} />
          </>
        )}

        {/* Historique — Modal courte (DS §12 : primitive Modal, pas de drawer) */}
        <HistoryModal targetType={targetType} targetId={targetId} permissionKey={historyKey} label={historyLabel} onClose={() => setHistoryKey(null)} />

        {/* Save Bar de la page — portée dans <body>, fixe bas-centre */}
        {dirty ? <PermissionsSaveBar count={pending.size} saving={saving} canSave={!(impact?.reasonRequired && !reason)} onCancel={cancelAll} onPreview={() => void doPreview()} onSave={() => void doApply()} /> : null}

        {/* Aperçu d'impact — Modal `owning` : elle porte son propre pied, elle passe devant la Save Bar */}
        {impact ? (
          <ImpactModal open={impactOpen} impact={impact} pendingCount={pending.size} roleMode={roleMode} reason={reason} saving={saving}
            onReason={setReason} onClose={() => setImpactOpen(false)} onExited={() => setImpact(null)} onConfirm={() => void doApply()} />
        ) : null}
      </main>
    </Shell>
  )
}

/** Libellé humain d'un leaf depuis l'arbre chargé (sous-titre de l'historique). */
function leafLabelOf(families: FamilyView[] | null, key: string): string | null {
  for (const f of families ?? []) {
    for (const l of f.looseLeaves) if (l.key === key) return l.label
    for (const m of f.modules) for (const l of m.leaves) if (l.key === key) return l.label
  }
  return null
}

/* ─────────── Save Bar de la page ───────────
   PORTAL vers <body> : `.lc-route` porte un transform (stacking context) — un `position: fixed`
   rendu à l'intérieur se comporte comme `absolute` et la barre atterrissait en bas du DOCUMENT
   (y ≈ 4600 px sur la page Temps). Couche `--z-savebar` : au-dessus des modales ordinaires,
   sous une modale `owning` et sous les toasts (bas-droite) ; centre-bas de la zone de contenu. */
function PermissionsSaveBar({ count, saving, canSave, onCancel, onPreview, onSave }: { count: number; saving: boolean; canSave: boolean; onCancel: () => void; onPreview: () => void; onSave: () => void }) {
  return createPortal(
    <div className="pm-savebar-zone">
      <div className="pm-savebar" role="status" aria-live="polite">
        <span className="pm-savebar__count">{count} modification(s) en attente</span>
        <div className="pm-savebar__actions">
          <button type="button" className="lc-btn" onClick={onCancel} disabled={saving}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--ghost" onClick={onPreview} disabled={saving}>Voir l’impact</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={saving || !canSave} onClick={onSave}>{saving ? 'Enregistrement…' : 'Enregistrer les permissions'}</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/* ─────────── Impact des modifications — Modal `owning` ─────────── */
function ImpactModal({ open, impact, pendingCount, roleMode, reason, saving, onReason, onClose, onExited, onConfirm }: { open: boolean; impact: Impact; pendingCount: number; roleMode: boolean; reason: string; saving: boolean; onReason: (r: string) => void; onClose: () => void; onExited: () => void; onConfirm: () => void }) {
  const reasonMissing = impact.reasonRequired && !reason.trim()
  return (
    <Modal open={open} onClose={onClose} onExited={onExited} ariaLabel="Impact des modifications" width={600} owning className="pm-impact-modal">
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: 'var(--grad-amber)' }}><ShieldAlert size={20} /></span>
        <div><div className="lc-modal__title">Impact des modifications</div><div className="pm-modal__sub">{plural(pendingCount, 'modification en attente', 'modifications en attente')} — ce qui change, avant d’enregistrer.</div></div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body pm-impact">
        {roleMode && impact.affectedUsers != null ? <p className="pm-impact__affected">{impact.affectedUsers} utilisateur(s) affecté(s).</p> : null}
        <ul className="pm-impact__lines">
          {impact.lines.map((l, i) => {
            const scopeChanged = l.scopeAfter !== undefined && !scopeEq(l.scopeBefore, l.scopeAfter)
            return (
              <li key={`${l.permissionKey}-${i}`}>
                <div className="pm-impact__row">
                  <span className="pm-impact__label">{l.label}{l.critical ? <span className="pm-badge pm-badge--critical"><ShieldAlert size={11} aria-hidden="true" /> CRITIQUE</span> : null}</span>
                  <span className="pm-impact__delta">{l.from === l.to ? <em>Périmètre modifié</em> : <><em>{frState(l.from)}</em> → <strong>{frState(l.to)}</strong></>}</span>
                </div>
                {scopeChanged ? (
                  <div className="pm-impact__scope">
                    <span>Périmètre actuel : <em>{scopeLabel(l.scopeBefore)}</em> → Nouveau : <strong>{scopeLabel(l.scopeAfter)}</strong></span>
                    <span className={`pm-impact__people${l.deltaPeople === null || (l.deltaPeople ?? 0) > 0 ? ' pm-impact__people--more' : ''}`}>{deltaPeopleLabel(l.deltaPeople)}</span>
                  </div>
                ) : null}
              </li>
            )
          })}
          {impact.lines.length === 0 ? <li className="lc-hint">Aucun changement effectif.</li> : null}
        </ul>
        {roleMode && impact.affectedUserScopes.length ? (
          <div className="pm-impact__users"><span className="pm-impact__users-title">Périmètre par porteur du rôle</span><div className="pm-chips">{impact.affectedUserScopes.map((u) => <span key={u.id} className="pm-chip pm-chip--team">{u.name} · {u.count === null ? 'tout le monde' : plural(u.count, 'personne', 'personnes')}</span>)}</div></div>
        ) : null}
        {impact.blocked ? <p className="pm-error" role="alert"><Lock size={14} aria-hidden="true" /> {impact.blocked}</p> : null}
        {impact.criticalCount > 0 ? <p className="pm-impact__critical"><ShieldAlert size={15} aria-hidden="true" /> {impact.criticalCount} permission(s) critique(s) — motif obligatoire et ré-authentification forte.</p> : null}
        {impact.stepUpRequired && impact.criticalCount === 0 ? <p className="pm-impact__critical"><Lock size={15} aria-hidden="true" /> Élargissement sensible du périmètre — ré-authentification forte demandée à l’enregistrement.</p> : null}
        {impact.reasonRequired ? (
          <Field label="Motif" helper={reasonMissing ? 'Obligatoire : ce motif est conservé dans l’historique des permissions.' : undefined}>
            <TextInput value={reason} onChange={onReason} placeholder="Pourquoi ce changement ?" ariaLabel="Motif du changement" maxLength={500} />
          </Field>
        ) : null}
      </div>
      <footer className="pm-modal__foot">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose} disabled={saving}>Fermer</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={saving || reasonMissing || impact.blocked !== null} onClick={onConfirm}>{saving ? 'Enregistrement en cours' : 'Confirmer et enregistrer'}</button>
      </footer>
    </Modal>
  )
}

function frState(s: string): string { return s === 'ALLOW' ? 'Autorisé' : s === 'DENY' ? 'Refusé' : s.startsWith('ALLOW') ? 'Autorisé' : 'Hérité' }
/** Delta de personnes d'un changement de périmètre : null = plus aucun filtre. */
function deltaPeopleLabel(d: number | null | undefined): string {
  if (d === null) return 'Tout le monde devient accessible'
  if (d === undefined) return ''
  if (d > 0) return `+${d} salarié${d > 1 ? 's' : ''} potentiellement concerné${d > 1 ? 's' : ''}`
  if (d < 0) return `${d} salarié${d < -1 ? 's' : ''} (accès retiré)`
  return 'Aucun salarié supplémentaire'
}

interface LeafProps { roleMode: boolean; targetType: 'USER' | 'ROLE'; targetId: string; effectiveState: (k: string, c: State) => State; pendingScope: (k: string) => Scope | undefined; onChange: (k: string, c: State, n: State) => void; onScope: (leaf: LeafView, s: Scope) => void; onHistory: (k: string) => void }

function GroupedLeaves({ leaves, ...rest }: { leaves: LeafView[] } & LeafProps) {
  const groups = useMemo(() => {
    const m = new Map<string, LeafView[]>()
    for (const l of leaves) { const g = l.group ?? 'AUTRES'; (m.get(g) ?? m.set(g, []).get(g)!).push(l) }
    return [...m.entries()]
  }, [leaves])
  return <>{groups.map(([g, ls]) => <LeafSection key={g} title={g} leaves={ls} {...rest} />)}</>
}

function LeafSection({ title, leaves, roleMode, targetType, targetId, effectiveState, pendingScope, onChange, onScope, onHistory }: { title: string; leaves: LeafView[] } & LeafProps) {
  return (
    <section className="pm-leafsection">
      <h3 className="pm-leafsection__title">{title}</h3>
      <div className="pm-leaves">
        {leaves.map((l) => {
          const st = effectiveState(l.key, l.configured)
          const chosen = pendingScope(l.key)
          const scopeNow: Scope | null = chosen ?? l.scope
          // Sélecteur de périmètre : permission « personnes », état AUTORISÉ (direct, ou hérité et effectif — l'override de scope créera l'ALLOW).
          const allowedNow = st === 'ALLOW' || (st === 'INHERITED' && l.configured === 'INHERITED' && l.effective === 'ALLOW')
          const showScope = l.scopeDomain === 'PERSON' && allowedNow
          return (
            <div key={l.key} id={`leaf-${l.key}`} className={`pm-leaf ${l.critical ? 'pm-leaf--critical' : ''}`}>
              <div className="pm-leaf__main">
                <div className="pm-leaf__title">
                  <strong>{l.label}</strong>
                  {l.critical ? <span className="pm-badge pm-badge--critical"><ShieldAlert size={11} aria-hidden="true" /> CRITIQUE</span> : null}
                  {l.risk && l.risk !== 'LOW' ? <span className={`pm-badge pm-badge--risk-${l.risk.toLowerCase()}`}>{RISK_LABEL[l.risk]}</span> : null}
                  {l.requiresStepup ? <span className="pm-badge pm-badge--stepup"><Lock size={11} aria-hidden="true" /> forte</span> : null}
                </div>
                <p className="pm-leaf__desc">{l.description}</p>
                <div className="pm-leaf__meta">
                  {!roleMode ? <span className={`pm-eff pm-eff--${l.effective.toLowerCase()}`}>{l.effective === 'ALLOW' ? 'Effectif : autorisé' : 'Effectif : refusé'}</span> : null}
                  {l.source ? <span className="pm-leaf__source">{l.source}</span> : null}
                  {scopeNow && !scopeIsAll(scopeNow) ? <span className="pm-badge pm-badge--scope">Périmètre : {scopeLabel(scopeNow)}{chosen ? ' (modifié)' : ''}</span> : null}
                  {l.actionBlocked ? <span className="pm-blocked"><AlertTriangle size={11} aria-hidden="true" /> {l.actionBlocked}</span> : null}
                  <button type="button" className="pm-leaf__hist" onClick={() => onHistory(l.key)}><History size={12} aria-hidden="true" /> Historique</button>
                </div>
              </div>
              <GateControl value={st} roleMode={roleMode} onChange={(s) => onChange(l.key, l.configured, s)} />
              {showScope ? <ScopeControl leaf={l} value={scopeNow} targetType={targetType} targetId={targetId} onChange={(s) => onScope(l, s)} /> : null}
              {!roleMode ? <WhyEffective leaf={l} targetId={targetId} /> : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}

/* ═══════════════ sélecteur de PÉRIMÈTRE (permissions « personnes ») ═══════════════ */
function ScopeControl({ leaf, value, targetType, targetId, onChange }: { leaf: LeafView; value: Scope | null; targetType: 'USER' | 'ROLE'; targetId: string; onChange: (s: Scope) => void }) {
  const selfOnly = leaf.scopeType === 'SELF'
  const roleMode = targetType === 'ROLE'
  // CORRECTNESS (Lot 4.7) : le moteur UNIONNE l'override utilisateur avec les scopes des rôles
  // (`scopesUnion([TEAM, ALL]) = ALL`). Un droit HÉRITÉ dont le périmètre effectif est déjà
  // « Tout le monde » ne peut donc pas être restreint ici : on ne propose pas ce qui n'aurait aucun effet.
  const lockedAll = !roleMode && leaf.configured === 'INHERITED' && leaf.effective === 'ALLOW' && scopeIsAll(leaf.scope)
  const shown: Scope | null = lockedAll ? { type: 'ALL' } : value
  const { kinds, ids } = kindsOf(shown)
  const [peopleOpen, setPeopleOpen] = useState(false)
  const people = usePeople(kinds.has('PERSON'))
  const preview = useScopePreview(targetType, targetId, leaf.key, shown ?? { type: 'ALL' }, scopeNeedsPreview(shown))

  const options: { k: PersonKind; label: string; icon: ReactNode; hint: string }[] = [
    { k: 'SELF', label: 'Lui-même', icon: <UserRound size={13} />, hint: 'Uniquement sa propre personne.' },
    { k: 'TEAM', label: 'Son équipe', icon: <Users size={13} />, hint: teamDoctrine(roleMode) },
    { k: 'ALL', label: 'Tout le monde', icon: <Globe size={13} />, hint: 'Aucun filtre : tous les salariés.' },
    { k: 'PERSON', label: 'Personnes spécifiques', icon: <UserCheck size={13} />, hint: 'Une liste nominative — exceptions ciblées, jamais générée.' },
  ]
  const toggle = (k: PersonKind) => {
    const next = new Set<PersonKind>(kinds)
    if (k === 'ALL' || k === 'PERSON') { next.clear(); next.add(k) }
    else {
      next.delete('ALL'); next.delete('PERSON')
      if (next.has(k)) { if (next.size > 1) next.delete(k) } else next.add(k)
    }
    onChange(scopeOf(next, k === 'PERSON' ? ids : []))
  }
  const addPerson = (id: string | null) => { if (id && !ids.includes(id)) onChange({ type: 'PERSON', ids: [...ids, id] }) }
  const removePerson = (id: string) => onChange({ type: 'PERSON', ids: ids.filter((x) => x !== id) })
  const teamPart = preview.data?.parts.find((p) => p.kind === 'TEAM')
  const labelOf = (id: string) => people.items.find((p) => p.value === id)?.label ?? 'Salarié'

  // SELF-only (`leave.request`) : un seul périmètre possible — un texte, pas un bouton grisé (DS §8).
  if (selfOnly) {
    return (
      <div className="pm-scope" role="group" aria-label={`Périmètre — ${leaf.label}`}>
        <p className="pm-scope__fixed"><UserRound size={13} aria-hidden="true" /> Périmètre : lui-même (ce droit ne porte que sur soi)</p>
      </div>
    )
  }

  return (
    <div className="pm-scope" role="group" aria-label={`Périmètre — ${leaf.label}`}>
      <div className="pm-scope__head"><span className="pm-scope__title">Périmètre</span><span className="pm-scope__hint">{lockedAll ? 'Périmètre hérité du rôle.' : 'Sur qui ce droit s’exerce. Lui-même et Son équipe se cumulent.'}</span></div>
      {lockedAll ? (
        <>
          <div className="pm-scope__opts">
            {options.filter((o) => o.k === 'ALL').map((o) => <span key={o.k} className="pm-scope__opt pm-scope__opt--all pm-scope__opt--static is-on" aria-current="true" title={o.hint}>{o.icon}<span>{o.label}</span></span>)}
          </div>
          <p className="pm-scope__fixed">Hérité : Tout le monde — un override ne peut qu’étendre ; restreignez au niveau du rôle.</p>
        </>
      ) : (
        <div className="pm-scope__opts">
          {options.map((o) => (
            <button key={o.k} type="button" className={`pm-scope__opt pm-scope__opt--${o.k.toLowerCase()}${kinds.has(o.k) ? ' is-on' : ''}`} aria-pressed={kinds.has(o.k)} title={o.hint} onClick={() => toggle(o.k)}>
              {o.icon}<span>{o.label}</span>
            </button>
          ))}
        </div>
      )}

      {kinds.has('TEAM') ? (
        <p className="pm-scope__doctrine">
          <strong>SON ÉQUIPE</strong> — {teamDoctrine(roleMode)}
          <span className="pm-scope__doctrine-hint">{teamDoctrineHint(roleMode)}{roleMode ? ' Pour un rôle, le périmètre dépend de chaque utilisateur qui le porte.' : ''}</span>
        </p>
      ) : null}

      {kinds.has('PERSON') ? (
        <div className="pm-scope__people">
          <PeoplePicker people={people} value={null} items={people.items.filter((p) => !ids.includes(p.value))} onChange={addPerson} placeholder="Ajouter un salarié…" ariaLabel="Ajouter une personne au périmètre" leading={<UserCheck size={14} />} />
          <div className="pm-chips">
            {ids.map((id) => <span key={id} className="pm-chip pm-chip--removable">{labelOf(id)}<button type="button" className="pm-chip__x" aria-label={`Retirer ${labelOf(id)}`} onClick={() => removePerson(id)}><X size={12} /></button></span>)}
            {ids.length === 0 ? <span className="lc-hint">Aucune personne sélectionnée — le droit ne s’exerce sur personne.</span> : null}
          </div>
        </div>
      ) : null}

      {scopeNeedsPreview(shown) ? (
        <div className="pm-scope__preview" aria-live="polite">
          {preview.status === 'loading' ? <span className="lc-hint">Calcul du périmètre…</span> : null}
          {preview.status === 'error' ? <span className="pm-scope__unavailable"><AlertTriangle size={13} aria-hidden="true" /> Aperçu indisponible : {preview.message ?? 'erreur'}</span> : null}
          {preview.status === 'ok' && preview.data ? (
            roleMode && preview.data.count === null ? (
              /* SELF / TEAM / UNION sur un rôle : impossible de donner UN compte — chaque porteur a le sien. */
              <>
                <span className="pm-scope__count">Le périmètre dépend de chaque utilisateur.</span>
                {preview.data.affectedUsers.length ? (
                  <div className="pm-chips">{preview.data.affectedUsers.map((u) => <span key={u.id} className="pm-chip pm-chip--team">{u.name} · {u.count === null ? 'tout le monde' : plural(u.count, 'personne', 'personnes')}</span>)}</div>
                ) : <span className="lc-hint">Aucun utilisateur ne porte ce rôle.</span>}
              </>
            ) : (
              <>
                <span className="pm-scope__count">{preview.data.count === null ? 'Tout le monde est accessible' : `${plural(preview.data.count, 'personne', 'personnes')} actuellement accessible${preview.data.count > 1 ? 's' : ''}`}</span>
                {teamPart ? (
                  <span className="pm-scope__parts">
                    {teamPart.teams && teamPart.teams.length ? <span className="pm-scope__teams">Équipes : {teamPart.teams.map((t) => <span key={t.team_id} className="pm-chip pm-chip--team">{t.name} · {t.count}</span>)}</span> : <span className="lc-hint">Aucune équipe dont il est responsable.</span>}
                    {typeof teamPart.direct === 'number' ? <span className="pm-scope__direct">Rattachements directs : {teamPart.direct}</span> : null}
                  </span>
                ) : null}
                {/* À 0 personne, l'action est ABSENTE, pas grisée (DS §8) : le compte au-dessus dit déjà tout. */}
                {preview.data.people.length > 0 ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setPeopleOpen(true)}><Users size={13} aria-hidden="true" /> Voir les personnes concernées</button> : null}
              </>
            )
          ) : null}
        </div>
      ) : null}

      <PeopleModal open={peopleOpen} onClose={() => setPeopleOpen(false)} title={`${leaf.label} — ${scopeLabel(shown)}`} people={preview.data?.people ?? []} />
    </div>
  )
}

/* ─────────── surface courte : les personnes d'un périmètre ─────────── */
function PeopleModal({ open, onClose, title, people }: { open: boolean; onClose: () => void; title: string; people: ScopePerson[] }) {
  const [q, setQ] = useState('')
  useEffect(() => { if (open) setQ('') }, [open])
  const nq = normalizeSearch(q)
  const shown = nq ? people.filter((p) => normalizeSearch(p.display_name).includes(nq) || p.via.some((v) => normalizeSearch(v).includes(nq))) : people
  return (
    <Modal open={open} onClose={onClose} ariaLabel="Personnes concernées" width={560} className="pm-people-modal">
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: 'var(--grad-blue)' }}><Users size={20} /></span>
        <div><div className="lc-modal__title">Personnes concernées</div><div className="pm-modal__sub">{title} · {plural(people.length, 'personne', 'personnes')}</div></div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body pm-people">
        {people.length > 50 ? (
          <div className="pm-people__search"><Search size={14} aria-hidden="true" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une personne…" aria-label="Rechercher une personne" /></div>
        ) : null}
        <ul className="pm-people__list">
          {shown.map((p) => <li key={p.person_id} className="pm-people__row"><strong>{p.display_name}</strong><span className="pm-people__via">{p.via.join(' · ') || '—'}</span></li>)}
          {shown.length === 0 ? <li className="lc-hint">{people.length === 0 ? 'Aucune personne dans ce périmètre.' : 'Aucun résultat.'}</li> : null}
        </ul>
      </div>
    </Modal>
  )
}

/* ═══════════════ explication effective : « Pourquoi ? » ═══════════════ */
function WhyEffective({ leaf, targetId }: { leaf: LeafView; targetId: string }) {
  const [open, setOpen] = useState(false)
  const [personId, setPersonId] = useState<string | null>(null)
  const [explain, setExplain] = useState<{ status: 'idle' | 'loading' | 'ok' | 'error'; allowed?: boolean | undefined; lines?: string[] | undefined; message?: string | undefined }>({ status: 'idle' })
  const personScoped = leaf.scopeDomain === 'PERSON' && !scopeIsAll(leaf.scope)
  const people = usePeople(open && personScoped)
  const preview = useScopePreview('USER', targetId, leaf.key, leaf.scope, open && leaf.effective === 'ALLOW' && scopeNeedsPreview(leaf.scope))

  useEffect(() => {
    if (!open || !personId) { setExplain({ status: 'idle' }); return }
    let alive = true
    setExplain({ status: 'loading' })
    void getShaped(`/api/v1/admin/users/${targetId}/permissions/${encodeURIComponent(leaf.key)}/explain?personId=${encodeURIComponent(personId)}`, (v) => isRecord(v) && Array.isArray(v['lines']) ? { allowed: v['allowed'] === true, lines: (v['lines'] as unknown[]).map(String) } : null)
      .then((r) => { if (!alive) return; setExplain(r.ok ? { status: 'ok', allowed: r.data.allowed, lines: r.data.lines } : { status: 'error', message: r.message ?? r.error }) })
    return () => { alive = false }
  }, [open, personId, targetId, leaf.key])

  const limit = (): string => {
    if (leaf.scopeDomain !== 'PERSON' || leaf.effective !== 'ALLOW') return ''
    const { kinds } = kindsOf(leaf.scope)
    if (kinds.has('ALL')) return ' · aucun filtre de personnes'
    if (preview.status === 'ok' && preview.data && preview.data.count !== null) return ` · limité aux ${plural(preview.data.count, 'personne', 'personnes')} de son périmètre`
    if (kinds.has('SELF') && kinds.size === 1) return ' · limité à sa propre personne'
    return ''
  }

  return (
    <div className="pm-why">
      <button type="button" className="pm-why__toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <HelpCircle size={13} aria-hidden="true" /> {leaf.effective === 'ALLOW' ? 'Pourquoi cette permission est effective ?' : 'Pourquoi cette permission est refusée ?'}
      </button>
      {open ? (
        <div className="pm-why__body">
          <p className="pm-why__line"><strong>{leaf.effective === 'ALLOW' ? 'Autorisé' : 'Refusé'}</strong>{leaf.source ? ` — ${leaf.source}` : ''}{leaf.scopeDomain === 'PERSON' && leaf.effective === 'ALLOW' ? ` · Périmètre : ${scopeLabel(leaf.scope)}` : ''}{limit()}</p>
          {leaf.actionBlocked ? <p className="pm-why__line pm-why__line--blocked"><AlertTriangle size={13} aria-hidden="true" /> {leaf.actionBlocked}</p> : null}
          {personScoped && leaf.effective === 'ALLOW' ? (
            <div className="pm-why__person">
              <span className="pm-why__label">Pourquoi cette personne ?</span>
              <PeoplePicker people={people} value={personId} items={people.items} onChange={setPersonId} clearable placeholder="Choisir un salarié…" ariaLabel="Expliquer l’accès à une personne" leading={<UserRound size={14} />} />
              {explain.status === 'loading' ? <span className="lc-hint">Analyse…</span> : null}
              {explain.status === 'error' ? <span className="pm-scope__unavailable"><AlertTriangle size={13} aria-hidden="true" /> Explication indisponible : {explain.message ?? 'erreur'}</span> : null}
              {explain.status === 'ok' ? (
                <ul className={`pm-why__lines${explain.allowed ? ' pm-why__lines--ok' : ' pm-why__lines--ko'}`}>
                  {(explain.lines ?? []).map((l, i) => <li key={i}>{l}</li>)}
                  {(explain.lines ?? []).length === 0 ? <li className="lc-hint">Aucune explication fournie.</li> : null}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/* ═══════════════ historique ═══════════════ */
interface HistoryEntry { id: string; createdAt: string; actorName: string | null; stateBefore: string | null; stateAfter: string | null; scopeBefore: Scope | null; scopeAfter: Scope | null; reason: string | null }
function shapeHistory(v: unknown): HistoryEntry[] | null {
  if (!isRecord(v) || !Array.isArray(v['history'])) return null
  return (v['history'] as unknown[]).flatMap((e): HistoryEntry[] => isRecord(e) && typeof e['id'] === 'string' ? [{
    id: e['id'], createdAt: typeof e['createdAt'] === 'string' ? e['createdAt'] : '', actorName: typeof e['actorName'] === 'string' ? e['actorName'] : null,
    stateBefore: typeof e['stateBefore'] === 'string' ? e['stateBefore'] : null, stateAfter: typeof e['stateAfter'] === 'string' ? e['stateAfter'] : null,
    scopeBefore: asScope(e['scopeBefore']), scopeAfter: asScope(e['scopeAfter']), reason: typeof e['reason'] === 'string' ? e['reason'] : null }] : [])
}
/**
 * Libellé d'un périmètre dans l'historique. Un scope absent ne veut pas dire
 * « tout le monde » : avant un premier override, le périmètre effectif était
 * celui des RÔLES (l'historique ne stocke que l'override) — on le dit tel quel.
 */
function historyScope(s: Scope | null, state: string | null): string {
  if (s) return scopeLabel(s)
  return state === 'INHERITED' || state === null ? 'Hérité des rôles' : 'Sans restriction'
}

/** Historique d'une permission — Modal COURTE (DS §12 : primitive Modal ; le drawer est interdit ici). */
function HistoryModal({ targetType, targetId, permissionKey, label, onClose }: { targetType: string; targetId: string; permissionKey: string | null; label: string | null; onClose: () => void }) {
  // La clé affichée survit à la fermeture : la surface garde son contenu le temps de sa sortie animée.
  const [shownKey, setShownKey] = useState<string | null>(permissionKey)
  useEffect(() => { if (permissionKey) setShownKey(permissionKey) }, [permissionKey])
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!shownKey) return
    let alive = true
    setEntries(null); setError(null)
    void getShaped(`/api/v1/admin/permissions/history?targetType=${targetType}&targetId=${targetId}&key=${encodeURIComponent(shownKey)}`, shapeHistory)
      .then((r) => { if (!alive) return; if (r.ok) setEntries(r.data); else { setEntries([]); setError(r.message ?? r.error) } })
    return () => { alive = false }
  }, [targetType, targetId, shownKey])
  return (
    <Modal open={permissionKey !== null} onClose={onClose} ariaLabel="Historique des modifications" width={600} className="pm-history-modal">
      <header className="lc-modal__head">
        <span className="lc-modal__icon" style={{ background: 'var(--grad-violet)' }}><History size={20} /></span>
        <div><div className="lc-modal__title">Historique</div><div className="pm-modal__sub">{label ?? shownKey ?? ''}</div></div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body pm-history">
        {error ? <p className="pm-error" role="alert"><AlertTriangle size={14} aria-hidden="true" /> Historique indisponible : {error}</p> : null}
        {entries === null && !error ? <p className="lc-hint">Chargement de l’historique…</p> : null}
        <ol className="pm-history__timeline">
          {(entries ?? []).map((e) => {
            const scopeChanged = (e.scopeBefore !== null || e.scopeAfter !== null) && !scopeEq(e.scopeBefore, e.scopeAfter)
            return (
              <li key={e.id}>
                <span className="pm-history__time">{e.createdAt ? new Date(e.createdAt).toLocaleString('fr-FR') : '—'}</span>
                <span className="pm-history__actor">{e.actorName ?? 'Système'}</span>
                <span className="pm-history__change">{frState(e.stateBefore ?? 'INHERITED')} → {frState(e.stateAfter ?? 'INHERITED')}</span>
                {scopeChanged ? <span className="pm-history__scope">Périmètre : {historyScope(e.scopeBefore, e.stateBefore)} → <strong>{historyScope(e.scopeAfter, e.stateAfter)}</strong></span> : e.scopeAfter ? <span className="pm-history__scope">Périmètre : {historyScope(e.scopeAfter, e.stateAfter)}</span> : null}
                {e.reason ? <span className="pm-history__reason">« {e.reason} »</span> : null}
              </li>
            )
          })}
          {entries && entries.length === 0 && !error ? <li className="lc-hint">Aucune modification enregistrée.</li> : null}
        </ol>
      </div>
    </Modal>
  )
}

/* ═══════════════ « Voir comme cet utilisateur » ═══════════════ */
interface PreviewNav { nav: { familyId: string; label: string; modules: { moduleId: string; label: string }[] }[]; targetName: string; scopes: { permission: string; label: string; scope: Scope | null; count: number | null }[] | null }

/**
 * Appel en CONTEXTE PRÉVISUALISATION : tout appel fait depuis cette page porte
 * `x-lc-preview: 1` — le serveur refuse alors toute mutation
 * (`PREVIEW_READ_ONLY`, §358), même en contournant l'interface. Évaluation
 * seule : aucune écriture, aucun changement de session.
 */
async function previewGet(path: string): Promise<unknown> {
  const r = await fetch(path, { credentials: 'same-origin', headers: { 'x-lc-preview': '1' } })
  const data = (await r.json().catch(() => ({}))) as unknown
  if (r.status === 401 && (!hasError(data) || data.error === 'UNAUTHENTICATED')) onUnauthenticated()
  return data
}
function shapePreviewNav(v: unknown): PreviewNav | null {
  if (!isRecord(v) || !Array.isArray(v['nav'])) return null
  const nav = (v['nav'] as unknown[]).flatMap((f) => isRecord(f) && typeof f['familyId'] === 'string' ? [{ familyId: f['familyId'], label: typeof f['label'] === 'string' ? f['label'] : f['familyId'],
    modules: Array.isArray(f['modules']) ? (f['modules'] as unknown[]).flatMap((m) => isRecord(m) && typeof m['moduleId'] === 'string' ? [{ moduleId: m['moduleId'], label: typeof m['label'] === 'string' ? m['label'] : m['moduleId'] }] : []) : [] }] : [])
  const scopes = Array.isArray(v['scopes']) ? (v['scopes'] as unknown[]).flatMap((s) => isRecord(s) && typeof s['permission'] === 'string' ? [{ permission: s['permission'], label: typeof s['label'] === 'string' ? s['label'] : s['permission'], scope: asScope(s['scope']), count: typeof s['count'] === 'number' ? s['count'] : null }] : []) : null
  return { nav, targetName: typeof v['targetName'] === 'string' ? v['targetName'] : 'utilisateur', scopes }
}

export function PreviewAsUserPage({ id }: { id: string }) {
  const meta = useShellMeta()
  const [data, setData] = useState<PreviewNav | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    void previewGet(`/api/v1/admin/users/${id}/effective-nav`).then((r) => {
      if (!alive) return
      if (hasError(r)) { setError(r.message ?? r.error); setData({ nav: [], targetName: 'utilisateur', scopes: null }); return }
      const shaped = shapePreviewNav(r)
      if (shaped) setData(shaped); else { setError('Réponse inattendue du serveur.'); setData({ nav: [], targetName: 'utilisateur', scopes: null }) }
    }).catch(() => { if (alive) { setError('Le serveur n’a pas répondu.'); setData({ nav: [], targetName: 'utilisateur', scopes: null }) } })
    return () => { alive = false }
  }, [id])
  return (
    <Shell active="settings" meta={meta}>
      <div className="pm-preview-banner"><Eye size={16} aria-hidden="true" /> PRÉVISUALISATION — {data?.targetName ?? '…'} · AUCUNE ACTION NE SERA EXÉCUTÉE <button type="button" className="pm-preview-exit" onClick={() => navigate(`/settings/users/${id}/permissions`)}>Quitter la prévisualisation</button></div>
      <main className="lc-page lc-stagger pm-page">
        <header className="pm-head"><div><h1 className="lc-title">Navigation de {data?.targetName ?? 'l’utilisateur'}</h1><p className="lc-subtitle">Exactement les familles, modules et fonctions que cette personne verrait — sans usurper son identité, sans possibilité d’action.</p></div></header>
        {error ? <p className="pm-error" role="alert"><AlertTriangle size={14} aria-hidden="true" /> Prévisualisation indisponible : {error}</p> : null}
        <div className="pm-preview-grid">
          <section className="pm-preview-nav" aria-label="Navigation simulée">
            {(data?.nav ?? []).map((f) => (
              <div key={f.familyId} className="pm-preview-fam">
                <strong>{f.label}</strong>
                <div className="pm-preview-mods">{f.modules.map((m) => <span key={m.moduleId} className="ac-chip">{m.label}</span>)}{f.modules.length === 0 ? <span className="lc-hint">Famille visible, aucun module détaillé.</span> : null}</div>
              </div>
            ))}
            {data && data.nav.length === 0 && !error ? <p className="lc-hint">Cet utilisateur ne verrait aucune famille.</p> : null}
          </section>
          <aside className="pm-preview-scopes" aria-label="Données visibles">
            <h2 className="pm-preview-scopes__title"><Users size={15} aria-hidden="true" /> Données visibles</h2>
            <p className="pm-preview-scopes__sub">Sur qui portent ses droits « personnes », aujourd’hui.</p>
            {data?.scopes === null || data?.scopes === undefined ? <p className="lc-hint">{data ? 'Périmètres non fournis par le serveur.' : 'Chargement…'}</p> : null}
            {data?.scopes && data.scopes.length === 0 ? <p className="lc-hint">Aucun droit portant sur des personnes.</p> : null}
            {data?.scopes && data.scopes.length > 0 ? (
              <ul className="pm-preview-scopes__list">
                {data.scopes.map((s) => (
                  <li key={s.permission} className="pm-preview-scope">
                    <strong>{s.label}</strong>
                    <span className="pm-preview-scope__scope">{scopeLabel(s.scope)}</span>
                    <span className="pm-preview-scope__count">{s.count === null ? 'tout le monde' : plural(s.count, 'personne', 'personnes')}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </aside>
        </div>
      </main>
    </Shell>
  )
}
